import React from 'react';
import { formatShortDate } from '../../utils/date-utils';
import { getCrewPartners } from '../../hooks/useInstallerJobs';
import { useInstaller } from '../../contexts/InstallerContext';

export default function JobCard({ job, onClick, isOverdue = false }) {
  const installer = useInstaller();

  const getJobTypeIcon = () => {
    if (job.isRepair) return '🔧';
    if (job.isDeliveryOnly) return '🚚';
    if (job.isPickupAtShop) return '📦';
    return '🔨';
  };

  const partners = getCrewPartners(job.assignedCrew, installer.name);

  // Calculate days overdue
  const getDaysOverdue = () => {
    if (!isOverdue || !job.scheduledDate) return 0;
    const scheduled = new Date(job.scheduledDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    scheduled.setHours(0, 0, 0, 0);
    const diffTime = today - scheduled;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysOverdue = getDaysOverdue();

  return (
    <div
      onClick={() => onClick(job)}
      style={{
        background: 'white',
        borderRadius: '16px',
        padding: '16px',
        marginBottom: '12px',
        boxShadow: isOverdue 
          ? '0 4px 12px rgba(245, 158, 11, 0.2)' 
          : '0 2px 8px rgba(0,0,0,0.08)',
        border: isOverdue 
          ? '2px solid #F59E0B' 
          : '1px solid rgba(0,0,0,0.06)',
        borderLeft: isOverdue 
          ? '6px solid #F59E0B' 
          : '1px solid rgba(0,0,0,0.06)',
        cursor: 'pointer',
        transition: 'all 0.2s',
        minHeight: '44px'
      }}
      onTouchStart={(e) => {
        e.currentTarget.style.transform = 'scale(0.98)';
        e.currentTarget.style.boxShadow = isOverdue
          ? '0 2px 6px rgba(245, 158, 11, 0.3)'
          : '0 1px 4px rgba(0,0,0,0.1)';
      }}
      onTouchEnd={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.boxShadow = isOverdue
          ? '0 4px 12px rgba(245, 158, 11, 0.2)'
          : '0 4px 12px rgba(0,0,0,0.12)';
      }}
    >
      {/* Overdue Badge */}
      {isOverdue && daysOverdue > 0 && (
        <div style={{
          display: 'inline-block',
          padding: '4px 10px',
          background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
          border: '1px solid #F59E0B',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '700',
          color: '#92400E',
          marginBottom: '10px'
        }}>
          ⚠️ {daysOverdue} day{daysOverdue !== 1 ? 's' : ''} overdue - Please mark complete
        </div>
      )}

      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px'
      }}>
        <div style={{
          fontSize: '32px',
          flexShrink: 0
        }}>
          {getJobTypeIcon()}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '4px'
          }}>
            <div style={{
              fontSize: '18px',
              fontWeight: '700',
              color: isOverdue ? '#dc2626' : '#8B4513'
            }}>
              #{job.contractNumber}
            </div>
            {job._dayInfo && (
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: '#f97316',
                background: 'rgba(249, 115, 22, 0.1)',
                padding: '2px 8px',
                borderRadius: '6px'
              }}>
                {job._dayInfo}
              </div>
            )}
          </div>

          <div style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2b2b2b',
            marginBottom: '8px'
          }}>
            {job.customerName || job.name}
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            fontSize: '14px',
            color: '#666'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>📍</span>
              <span>{job.address || `${job.name}`}</span>
            </div>

            <div style={{
              display: 'flex',
              gap: '16px',
              flexWrap: 'wrap'
            }}>
              {job.windowCount > 0 && (
                <span>🪟 {job.windowCount} window{job.windowCount !== 1 ? 's' : ''}</span>
              )}
              {job.doorCount > 0 && (
                <span>🚪 {job.doorCount} door{job.doorCount !== 1 ? 's' : ''}</span>
              )}
              {job.durationDays && (
                <span>🕐 {job.durationDays} day{job.durationDays !== 1 ? 's' : ''}</span>
              )}
            </div>

            {partners && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '4px',
                padding: '6px 12px',
                background: 'rgba(139, 69, 19, 0.1)',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#8B4513'
              }}>
                <span>👷</span>
                <span>Working with: {partners}</span>
              </div>
            )}

            <div style={{
              fontSize: '13px',
              color: isOverdue ? '#dc2626' : '#999',
              fontWeight: isOverdue ? '600' : 'normal',
              marginTop: '4px'
            }}>
              {isOverdue ? 'Was scheduled: ' : 'Scheduled: '}
              {formatShortDate(job.scheduledDate)}
            </div>
          </div>
        </div>

        <div style={{
          fontSize: '20px',
          color: isOverdue ? '#F59E0B' : '#8B4513',
          flexShrink: 0
        }}>
          →
        </div>
      </div>
    </div>
  );
}